import Phaser from 'phaser';
import { PALETTE, FONT, px } from '../theme';
import { onTap } from './tap';

/** Eine Tafel im Seekarten-Stil. */
export function createPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
): Phaser.GameObjects.Rectangle {
  const panel = scene.add.rectangle(x, y, width, height, PALETTE.panel, 0.9).setOrigin(0, 0);
  panel.setStrokeStyle(1, PALETTE.panelEdge);
  return panel;
}

export interface ListRowOptions {
  readonly width: number;
  readonly height?: number;
  readonly title: string;
  readonly detail: string;
  /** Kurzer Hinweis rechts, etwa die erwartete Stufe. */
  readonly badge?: string;
  readonly badgeColor?: string;
  readonly onClick?: () => void;
  readonly selected?: boolean;
}

/**
 * Eine anklickbare Zeile mit Titel, Zusatzangaben und optionaler Markierung.
 * Traegt die Notruf-, Fahrzeug- und Hinweislisten der Kampagnenszenen.
 */
export function createListRow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  options: ListRowOptions,
): Phaser.GameObjects.Container {
  const padding = 12;

  // Der Rahmen entsteht erst, wenn die Hoehe feststeht — siehe unten. Bis
  // dahin sammeln wir die Kinder ohne ihn.
  const children: Phaser.GameObjects.GameObject[] = [];

  // Das Badge zuerst anlegen, um seine Breite zu kennen: Titel und Detailzeile
  // muessen daneben passen, sonst laufen sie darunter hindurch.
  let badgeWidth = 0;
  if (options.badge !== undefined) {
    const badge = scene.add
      .text(options.width - padding, (options.height ?? 46) / 2, options.badge, {
        fontFamily: FONT.mono,
        fontSize: px(12),
        color: options.badgeColor ?? PALETTE.inkDim,
      })
      .setOrigin(1, 0.5);
    badgeWidth = badge.width + padding;
    children.push(badge);
  }

  // Verfuegbare Textbreite. Ohne diese Begrenzung lief die Detailzeile bisher
  // ueber den Rahmen hinaus — bei den Hintergrund-Beschreibungen im
  // Charaktereditor deutlich sichtbar.
  const textWidth = Math.max(40, options.width - padding * 2 - badgeWidth);

  const title = scene.add.text(padding, 8, options.title, {
    fontFamily: FONT.mono,
    fontSize: px(13),
    color: PALETTE.ink,
    wordWrap: { width: textWidth },
    // Titel bleiben einzeilig; laufen sie doch einmal ueber, wird gekuerzt
    // statt die Zeile zu sprengen.
    maxLines: 1,
  });

  const detail = scene.add.text(padding, 8 + title.height + 4, options.detail, {
    fontFamily: FONT.mono,
    fontSize: px(11),
    color: PALETTE.inkFaint,
    wordWrap: { width: textWidth },
    lineSpacing: 2,
  });

  /*
   * Die Zeile waechst mit ihrem Text.
   *
   * Frueher war die Hoehe fest, und die Aufrufer gaben sie in Pixeln vor. Das
   * ging, solange die Schrift immer gleich gross war — mit der Vergroesserung
   * aus den Einstellungen lief die Detailzeile aus dem Rahmen heraus. Die
   * angegebene Hoehe ist deshalb jetzt eine Untergrenze, kein Versprechen.
   */
  const needed = 8 + title.height + 4 + detail.height + 8;
  const height = Math.max(options.height ?? 46, needed);

  const frame = scene.add.rectangle(0, 0, options.width, height, PALETTE.panel, 0.55).setOrigin(0, 0);
  frame.setStrokeStyle(1, options.selected === true ? PALETTE.coast : PALETTE.panelEdge);

  children.unshift(frame);
  children.push(title, detail);

  const container = scene.add.container(x, y, children);
  container.setSize(options.width, height);

  if (options.onClick !== undefined) {
    // Der Container hat seinen Ursprung links oben, die Trefferflaeche wird in
    // Phaser aber um den Mittelpunkt herum ausgewertet.
    container.setInteractive(
      new Phaser.Geom.Rectangle(options.width / 2, height / 2, options.width, height),
      Phaser.Geom.Rectangle.Contains,
    );
    container.input!.cursor = 'pointer';
    onTap(container, {
      onTap: () => options.onClick?.(),
      onHighlight: (on) =>
        frame.setStrokeStyle(
          1,
          on || options.selected === true ? PALETTE.coast : PALETTE.panelEdge,
        ),
    });
  }

  return container;
}
